import { useQuery } from '@tanstack/react-query';
import { Make, MAKE_ARRAY, RideType } from 'models/shared';
import { useMemo } from 'react';

export default function useBikes(
  years: string[] = [],
  makes: (Make | '')[] = [],
  types: (RideType | '')[] = []
) {
  const { data: bikes } = useQuery({
    queryKey: ['bikes'],
    queryFn: async () => {
      try {
        const res = await fetch(
          // 'https://storage.googleapis.com/mx-locker-staging.appspot.com/common-files/vehicle-models-with-years-merged.json'
          'https://bundle.mxlocker.com/vehicle-models-with-years.json'
        );
        if (!res.ok) throw new Error('Failed to fetch bikes from json bundle');
        const data = (await res.json()) as Record<
          RideType,
          Record<
            Make,
            {
              model: string;
              years: number[];
            }[]
          >
        >;
        return data;
      } catch {
        return {} as Record<
          RideType,
          Record<
            Make,
            {
              model: string;
              years: number[];
            }[]
          >
        >;
      }
    },
  });
  const bikeYears = useMemo(() => {
    return Array.from(
      { length: new Date().getFullYear() + 2 - 1960 },
      (_, i) => 1960 + i
    ).sort((a, b) => b - a);
  }, []);

  const allMakes = useMemo(() => {
    return MAKE_ARRAY;
  }, []);

  const bikeMakes = useMemo(() => {
    try {
      if (!types.length || !bikes) return MAKE_ARRAY;
      const obj = types
        .map((type) => {
          if (!type) return [];
          if (!bikes[type]) return [];
          return Object.keys(bikes[type]);
        })
        .flat();
      if (!obj) return [];
      return Array.from(new Set(obj.sort()));
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [bikes, types]);

  const bikeModels = useMemo(() => {
    try {
      if (!types.length || !bikes || !makes.length || !years.length) return [];
      const obj1 = types
        .map((type) => (type ? bikes[type] : []))
        .flat() as Record<
        Make,
        {
          model: string;
          years: number[];
        }[]
      >[];

      if (!obj1) return [];

      const obj2 = makes
        .map((make) => {
          if (!make) return [];
          const found = obj1.find((obj) => obj && obj[make]);
          if (!found || !found[make]) return [];
          return found[make]
            .filter(
              (m) =>
                !years.length ||
                m.years.some((y) => years.includes(y.toString()))
            )
            .map((m) => m.model);
        })
        .flat()
        .filter(Boolean) as string[];

      if (!obj2) return [];

      return obj2.sort();
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [types, bikes, makes, years]);

  const bikeModelsByMake: Record<
    Make,
    {
      model: string;
      years: number[];
    }[]
  > = useMemo(() => {
    try {
      if (!types.length || !bikes || !makes.length || !years.length)
        return {} as Record<
          Make,
          {
            model: string;
            years: number[];
          }[]
        >;
      const obj1 = types
        .map((type) => (type ? bikes[type] : []))
        .flat() as Record<
        Make,
        {
          model: string;
          years: number[];
        }[]
      >[];
      const obj2 = obj1.reduce(
        (acc, curr) => {
          makes.forEach((make) => {
            const m = make as Make | '';
            if (!m) return acc;
            if (!acc[m]) acc[m] = [];
            if (years.length) {
              if (curr[m]) {
                acc[m].push(
                  ...curr[m].filter((m) =>
                    m.years.some((y) => years.includes(y.toString()))
                  )
                );
              }
            } else {
              if (curr[m]) {
                acc[m].push(...curr[m]);
              }
            }
          });
          return acc;
        },
        {} as Record<
          Make,
          {
            model: string;
            years: number[];
          }[]
        >
      );
      return obj2;
    } catch (e) {
      console.error(e);
      return {} as Record<
        Make,
        {
          model: string;
          years: number[];
        }[]
      >;
    }
  }, [bikes, types, makes, years]);

  return {
    bikeYears,
    bikeMakes,
    bikeModels,
    bikeModelsByMake,
    bikes,
    allMakes,
  };
}
